import { eachDayOfInterval, endOfDay, startOfDay } from "date-fns";
import type {
  CheckoutFeedback,
  CommunicationFeedback,
  RequestFeedback,
} from "@/services/interfaces/IFeedbackService";
import {
  formatLocalDateParam,
  parseLocalDateParam,
} from "../../utils/analyticsDateUtils";
import { DEFAULT_CATEGORY_KEYS } from "./reviewsTabDefaults";
import type {
  CategoryKey,
  CategoryRatingRow,
  PreCheckoutTrendPoint,
  ReviewTableRow,
} from "./types";

export const REVIEWS_FEEDBACK_FETCH_LIMIT = 500;

export function toFeedbackDateRange(from: string, to: string) {
  const start = parseLocalDateParam(from);
  const end = parseLocalDateParam(to);

  if (!start || !end) {
    return null;
  }

  return {
    feedbackStartDateRange: startOfDay(start).toISOString(),
    feedbackEndDateRange: endOfDay(end).toISOString(),
  };
}

function formatGuestName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "—";
}

function formatFeedbackDateTime(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(String(value));

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${formattedDate}, ${formattedTime}`;
}

type FeedbackTableSource = {
  id: string;
  rating: number;
  comment: string | null;
  createdAt: Date | string;
};

type GuestProfileSource = {
  firstName?: string | null;
  lastName?: string | null;
  language?: string;
};

function mapFeedbacksToTableRows<T extends FeedbackTableSource>(
  feedbacks: T[],
  getGuestProfile: (feedback: T) => GuestProfileSource | undefined,
): ReviewTableRow[] {
  return feedbacks.map((feedback) => {
    const profile = getGuestProfile(feedback);

    return {
      id: feedback.id,
      guestName: formatGuestName(profile?.firstName, profile?.lastName),
      countryCode: profile?.language ?? "",
      feedbackDate: formatFeedbackDateTime(feedback.createdAt),
      feedback: feedback.comment?.trim() || "—",
      rating: feedback.rating,
    };
  });
}

type GuestLinkedFeedback = FeedbackTableSource & {
  guest?: { profile?: GuestProfileSource };
};

function mapGuestLinkedFeedbacksToTableRows(
  feedbacks: GuestLinkedFeedback[],
): ReviewTableRow[] {
  return mapFeedbacksToTableRows(
    feedbacks,
    (feedback) => feedback.guest?.profile,
  );
}

export function mapCheckoutFeedbacksToTableRows(
  feedbacks: CheckoutFeedback[],
): ReviewTableRow[] {
  return mapGuestLinkedFeedbacksToTableRows(feedbacks);
}

export function mapRequestFeedbacksToTableRows(
  feedbacks: RequestFeedback[],
): ReviewTableRow[] {
  return mapFeedbacksToTableRows(
    feedbacks,
    (feedback) => feedback.request.guest?.profile,
  );
}

export function mapCommunicationFeedbacksToTableRows(
  feedbacks: CommunicationFeedback[],
): ReviewTableRow[] {
  return mapGuestLinkedFeedbacksToTableRows(feedbacks);
}

function aggregateCategoryBreakdown(
  feedbacks: CheckoutFeedback[],
  category: CategoryKey,
): CategoryRatingRow[] {
  const counts = new Map<number, number>();

  for (let star = 1; star <= 5; star += 1) {
    counts.set(star, 0);
  }

  for (const feedback of feedbacks) {
    const raw = feedback.categoriesRating?.[category];
    if (raw == null || raw === "") {
      continue;
    }

    const score = Number(raw);
    if (!Number.isFinite(score)) {
      continue;
    }

    const star = Math.max(1, Math.min(5, Math.round(score)));
    counts.set(star, (counts.get(star) ?? 0) + 1);
  }

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);

  return [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    percent:
      total > 0 ? Math.round(((counts.get(stars) ?? 0) / total) * 100) : 0,
  }));
}

export function mapCheckoutFeedbacksToCategoryBreakdowns(
  feedbacks: CheckoutFeedback[],
): Record<CategoryKey, CategoryRatingRow[]> {
  return DEFAULT_CATEGORY_KEYS.reduce(
    (breakdowns, category) => {
      breakdowns[category] = aggregateCategoryBreakdown(feedbacks, category);
      return breakdowns;
    },
    {} as Record<CategoryKey, CategoryRatingRow[]>,
  );
}

function isPreCheckoutReview(
  createdAt: Date,
  departureDate: string | Date,
): boolean {
  const departure =
    departureDate instanceof Date
      ? departureDate
      : new Date(String(departureDate));

  if (Number.isNaN(departure.getTime())) {
    return false;
  }

  return createdAt <= endOfDay(departure);
}

export function buildPreCheckoutTrendFromCheckoutFeedbacks(
  feedbacks: CheckoutFeedback[],
  from: string,
  to: string,
): PreCheckoutTrendPoint[] {
  const start = parseLocalDateParam(from);
  const end = parseLocalDateParam(to);

  if (!start || !end || start > end) {
    return [];
  }

  const days = eachDayOfInterval({ start, end });
  const buckets = new Map<string, { pre: number; post: number }>();

  for (const day of days) {
    buckets.set(formatLocalDateParam(day), { pre: 0, post: 0 });
  }

  for (const feedback of feedbacks) {
    const createdAt =
      feedback.createdAt instanceof Date
        ? feedback.createdAt
        : new Date(String(feedback.createdAt));

    if (Number.isNaN(createdAt.getTime())) {
      continue;
    }

    const dayKey = formatLocalDateParam(startOfDay(createdAt));
    const bucket = buckets.get(dayKey);

    if (!bucket) {
      continue;
    }

    const departureDate = feedback.booking?.departureDate;
    if (!departureDate) {
      continue;
    }

    if (isPreCheckoutReview(createdAt, departureDate)) {
      bucket.pre += 1;
    } else {
      bucket.post += 1;
    }
  }

  return days.map((day) => {
    const dayKey = formatLocalDateParam(day);
    const bucket = buckets.get(dayKey) ?? { pre: 0, post: 0 };

    return {
      day: dayKey,
      preCheckoutReviews: bucket.pre,
      postStayReviews: bucket.post,
    };
  });
}
